import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@ui/Button';
import ComboBox, { ComboBoxOption } from '@ui/ComboBox';
import { getCheapestEstimate } from '@util/firestore/shipengine';
import { formatCurrency } from '@util/index';
import { getCountries } from '@util/types/locations';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import BaseModal from './BaseModal';
import { useToastContext } from 'context/ToastContext';

interface EstimateShippingModalProps {
  product_id: string;
  isOpen: boolean;
  dismiss: (
    val: {
      cost: number;
      code: string;
      name: string;
      estimated: true;
    } | null
  ) => void;
}
const estimateShippingSchema = z.object({
  country: z.string(),
  postal_code: z.string().min(4),
});
type EstimateShippingForm = z.infer<typeof estimateShippingSchema>;

export default function EstimateShippingModal({
  product_id,
  isOpen,
  dismiss,
}: EstimateShippingModalProps) {
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState<number | null>(null);
  const {
    handleSubmit,
    control,
    register,
    setValue,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm<EstimateShippingForm>({
    defaultValues: {
      country: 'US',
    },
    resolver: zodResolver(estimateShippingSchema),
  });

  const data = watch();
  const { showErrorToast } = useToastContext();

  const onSubmit = async (form: EstimateShippingForm) => {
    setLoading(true);
    try {
      const cheapest = await getCheapestEstimate(
        product_id,
        form.postal_code,
        form.country
      );
      setShippingCost(cheapest);
    } catch (e) {
      setError('postal_code', {
        message: 'Shipping is not available for this postal code and country',
      });
      showErrorToast(
        'Shipping is not available for this postal code and country'
      );
    }

    setLoading(false);
  };

  function handleComboBoxChange(optionValue: ComboBoxOption) {
    setValue('country', optionValue.value);
    trigger('country');
  }

  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={() =>
        dismiss(
          shippingCost
            ? {
                cost: shippingCost,
                code: data.country,
                name: data.country,
                estimated: true,
              }
            : null
        )
      }
      title="Estimate Shipping"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-8">
        <div className="flex w-[30rem] flex-col gap-[2.4rem] p-5">
          {shippingCost === null ? (
            <div className="flex flex-col gap-6">
              <FormLabel value="Country" required={true}>
                <Controller
                  rules={{ required: true }}
                  control={control}
                  name="country"
                  render={() => (
                    <ComboBox
                      autoComplete="country-name"
                      allowEmpty={false}
                      options={getCountries()}
                      error={!!errors.country}
                      onChange={handleComboBoxChange}
                      // value={form.businessInfo?.country}
                    />
                  )}
                />
              </FormLabel>
              <FormLabel
                value="Postal Code"
                errorMessage={errors.postal_code?.message}
              >
                <FormInput
                  autoComplete="postal-code"
                  placeholder="90045"
                  error={!!errors.country}
                  {...register('postal_code')}
                />
              </FormLabel>
            </div>
          ) : (
            <div className="my-[5rem] text-center ">
              <h1 className="mb-5 text-7xl font-bold">
                {formatCurrency(shippingCost)}
              </h1>
              <h3>Estimated Cost</h3>
            </div>
          )}
          <div className="flex flex-col gap-[1.6rem]">
            {shippingCost === null && (
              <Button
                buttonType="submit"
                text="Get Estimate"
                type="secondary"
                loading={loading}
              />
            )}
            <div className="font-semibold text-brand-secondary">
              <Button
                text={shippingCost === null ? 'Cancel' : 'Close'}
                type="text"
                width="fluid"
                onClick={() =>
                  dismiss(
                    shippingCost
                      ? {
                          cost: shippingCost,
                          code: data.country,
                          name: data.country,
                          estimated: true,
                        }
                      : null
                  )
                }
              />
            </div>
          </div>
        </div>
      </form>
    </BaseModal>
  );
}
